import {
  formatFileSize,
  normalizeVideoDuration,
  validateVideoDuration,
} from '../utils/videoValidation';
import type {
  PickerVideoFallback,
  PrepareVideoOptions,
  PrepareVideoResult,
  VideoFileInfo,
} from './videoOptimization.types';

function loadHtmlVideoMetadata(uri: string): Promise<{ duration: number; width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = uri;
    video.onloadedmetadata = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 0;
      resolve({
        duration,
        width: video.videoWidth || 0,
        height: video.videoHeight || 0,
      });
      video.src = '';
    };
    video.onerror = () => {
      reject(new Error('UNREADABLE_METADATA'));
    };
  });
}

async function readBlobSize(uri: string, fallbackSize?: number): Promise<number> {
  if (fallbackSize && fallbackSize > 0) {
    return fallbackSize;
  }
  try {
    const response = await fetch(uri);
    const blob = await response.blob();
    return blob.size || 0;
  } catch {
    return 0;
  }
}

export async function inspectVideo(
  uri: string,
  fallback?: PickerVideoFallback
): Promise<VideoFileInfo> {
  let duration = normalizeVideoDuration(fallback?.duration);
  let width = fallback?.width ?? 0;
  let height = fallback?.height ?? 0;

  try {
    const htmlMeta = await loadHtmlVideoMetadata(uri);
    duration = htmlMeta.duration || duration;
    width = htmlMeta.width || width;
    height = htmlMeta.height || height;
  } catch (error) {
    console.warn('[VideoOptimization] Web metadata read failed, using picker fallback:', error);
  }

  const sizeBytes = await readBlobSize(uri, fallback?.fileSize);

  if (!duration) {
    throw new Error('UNREADABLE_METADATA');
  }

  return { uri, duration, width, height, sizeBytes };
}

export function cancelVideoOptimization(_cancellationId: string): void {
  // Native compression is not available on web.
}

export async function cleanupOptimizedVideo(_uri?: string | null): Promise<void> {
  // Web uses the original object URL / blob from the picker.
}

export async function prepareVideoForUpload({
  uri,
  fallback,
  onProgress,
  maxDuration,
}: PrepareVideoOptions): Promise<PrepareVideoResult> {
  let original: VideoFileInfo;

  try {
    original = await inspectVideo(uri, fallback);
  } catch (error) {
    console.warn('[VideoOptimization] Unable to inspect video on web:', error);
    return {
      status: 'error',
      title: 'Unable to read video',
      message: 'This video file could not be processed. Please try a different video.',
    };
  }

  const durationCheck = validateVideoDuration(original.duration, { max: maxDuration });
  if (!durationCheck.ok) {
    return {
      status: 'error',
      title: durationCheck.title,
      message: durationCheck.message,
    };
  }

  onProgress?.(0.35);
  await new Promise((resolve) => setTimeout(resolve, 250));
  onProgress?.(1);

  console.log('[VideoOptimization] Web skip native compress. Original:', {
    duration: `${original.duration.toFixed(1)} seconds`,
    resolution: `${original.width} x ${original.height}`,
    size: formatFileSize(original.sizeBytes),
  });

  return {
    status: 'success',
    uri: original.uri,
    original,
    optimized: original,
    wasOptimized: false,
    reductionPercent: 0,
  };
}
